import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, downloadBlob, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, CheckSquare, Square, FileDown, PenLine } from "lucide-react";
import Logo from "@/components/Logo";
import { bandColor } from "@/lib/forms";
import { DraftBanner, BANNER_TEXT, TEMPLATE_DISCLAIMER } from "@/components/forms/FormBadges";
import { toast } from "sonner";

function Value({ f, v }) {
  if (f.type === "rating") return <span className="font-semibold text-[#0A2A5E]">{v ? `${v} / 5` : "—"}</span>;
  if (f.type === "checkbox") return v ? <CheckSquare className="w-4 h-4 text-emerald-600 inline" /> : <Square className="w-4 h-4 text-slate-400 inline" />;
  return <span className="whitespace-pre-wrap">{v || <span className="text-slate-400">—</span>}</span>;
}

export default function SubmissionView() {
  const { submissionId } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [t, setT] = useState(null);

  useEffect(() => {
    // The record renders with the template version that was used at the time (snapshot), never today's template
    api.get(`/forms/submissions/${submissionId}`).then(async (r) => {
      setS(r.data);
      if (r.data.template_snapshot) { setT({ ...r.data.template_snapshot, id: r.data.template_id }); return; }
      const tt = await api.get(`/forms/templates/${r.data.template_id}`);
      setT(tt.data);
    }).catch((e) => toast.error(apiErrorMessage(e, "Record not found")));
  }, [submissionId]);

  const pdf = async () => {
    try { await downloadBlob(`/forms/submissions/${submissionId}/pdf`, `${s.template_id}-${submissionId.slice(0, 8)}.pdf`); toast.success("PDF downloaded"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not generate PDF")); }
  };

  if (!s || !t) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const isOwnerDraft = s.status === "draft" && s.submitted_by === user?.id;
  const bannerStatus = BANNER_TEXT[t.status] ? t.status : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="no-print flex items-center justify-between mb-4">
        <Link to="/app/forms" data-testid="back-to-forms" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0A2A5E]"><ArrowLeft className="w-4 h-4" /> All forms</Link>
        <div className="flex flex-wrap gap-2">
          {isOwnerDraft && <Link to={`/app/forms/fill/${s.template_id}?draft=${s.id}`}><Button data-testid="continue-draft-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><PenLine className="w-4 h-4 mr-2" aria-hidden="true" /> Continue draft</Button></Link>}
          <Button data-testid="download-pdf-btn" onClick={pdf} variant="outline" className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"><FileDown className="w-4 h-4 mr-2" aria-hidden="true" /> Download PDF</Button>
          <Button data-testid="print-submission-btn" onClick={() => window.print()} variant="outline" className="border-slate-300"><Printer className="w-4 h-4 mr-2" aria-hidden="true" /> Print</Button>
        </div>
      </div>
      {bannerStatus && <div className="mb-4"><DraftBanner status={bannerStatus} /></div>}

      <Card data-testid="submission-view" className="print-area p-6 sm:p-10 bg-white border-slate-200">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-5">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <div className="font-display font-extrabold text-[#0A2A5E]">LUMIS HR</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C7761A] font-semibold">{t.category}</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>{s.status === "submitted" ? "Submitted" : "Saved"} {new Date(s.submitted_at || s.created_at).toLocaleString()}</div>
            <div>by {s.submitted_by_name} ({s.submitted_by_role})</div>
            <div data-testid="record-state" className={`uppercase font-bold mt-1 ${s.status === "draft" ? "text-slate-600" : "text-emerald-700"}`}>{s.status === "draft" ? "Private draft — visible only to you" : "Submitted"}</div>
            {s.status === "submitted" && s.recipient_group && <div className="mt-1" data-testid="record-recipients">Sent to {s.recipient_group}{s.recipient_names?.length ? `: ${s.recipient_names.join(", ")}` : ""}</div>}
            <div className="mt-1">Template version {t.version || "1.0"}</div>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold text-[#0A2A5E] mt-5">{t.title}</h1>
        {s.subject_name && <div className="text-slate-700 mt-1">Subject: <span className="font-semibold">{s.subject_name}</span></div>}

        {s.score && (
          <div data-testid="submission-score" className="mt-4 p-4 rounded-xl bg-[#F5F9FE] border border-slate-200 flex flex-wrap items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Weighted score</div>
              <div className="font-display text-3xl font-extrabold text-[#0A2A5E]">{s.score.percent}%</div>
              <div className="text-xs text-slate-500">{s.score.earned} / {s.score.possible} points</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${bandColor(s.score.percent)}`}>{s.score.band}</span>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {t.sections.map((sec, i) => (
            <div key={sec.title || i} className="print-break">
              <h2 className="font-display font-bold text-[#0A2A5E] mb-2 flex items-center gap-2"><span className="w-1.5 h-4 rounded-full bg-[#F5A623]" />{sec.title}</h2>
              <dl className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {sec.fields.map((f) => (
                  <div key={f.id} className="grid sm:grid-cols-3 gap-2 px-3 py-2 text-sm">
                    <dt className="text-slate-500">{f.label}</dt>
                    <dd className="sm:col-span-2 text-slate-800"><Value f={f} v={s.answers[f.id]} /></dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        {t.source?.citation && <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500">Reference guide: {t.source.citation} — {t.source.source_url}</div>}
        {t.purpose_note && <p className="mt-4 text-xs text-slate-600 italic">{t.purpose_note}</p>}
        <div className="mt-6 pt-3 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed" data-testid="record-disclaimer">
          <p>{TEMPLATE_DISCLAIMER}</p>
          <p className="mt-1">LUMIS HR Original Template · Created by LUMIS HR Solutions LLC · © 2026 LUMIS HR Solutions LLC. All rights reserved.</p>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-8 text-xs text-slate-500">
          <div><div className="border-b border-slate-400 h-8" /><div className="mt-1">Employee / Candidate signature & date</div></div>
          <div><div className="border-b border-slate-400 h-8" /><div className="mt-1">Supervisor / Interviewer signature & date</div></div>
        </div>
      </Card>
    </div>
  );
}
